import { Component, Inject, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTabGroup as MatTabGroup } from '@angular/material/legacy-tabs';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

import { EWeightUnit } from '../../../invoice/interfaces/shipstation-shipment.interface';
import { SnackBarConfig } from '../../../shared';
import {
  IWeight,
  OptionInterface,
} from '../../../shared/interfaces/common.interface';
import { IWarehouse } from '../../../shared/interfaces/location-warehouse.interface';
import {
  SystemSettingApiService,
  LocationApiService,
  ShipstationApiService,
} from '../../../shared/services';
import { SystemSettingInterface } from '../../../system-setting';

import {
  IShipStationCarriers,
  IShipstationPackagesServices,
  IShipstationRates,
} from './../../interfaces';

interface IToShippingAddress {
  toPostalCode :string;
  toCountry :string;
  toState ?:string;
  toCity ?:string;
}

@Component({
  selector: 'lum-shipping-rates',
  templateUrl: './shipping-rates.component.html',
  styleUrls: ['./shipping-rates.component.scss'],
})
export class ShippingRatesComponent implements OnInit {
  form :UntypedFormGroup;
  warehouse :IWarehouse;
  carriers :IShipStationCarriers[];
  packages :IShipstationPackagesServices[] = [];
  services :IShipstationPackagesServices[] = [];
  rates :IShipstationRates[] = [];
  selectedRate :IShipstationRates;
  loading :boolean = false;
  loadingRates :boolean = false;
  loading$ :Observable<boolean>;
  warehouse$ :Observable<IWarehouse>;
  carriers$ :Observable<IShipStationCarriers[]>;
  packages$ :Observable<IShipstationPackagesServices[]>;
  services$ :Observable<IShipstationPackagesServices[]>;
  rates$ :Observable<IShipstationRates[]>;
  loadingRates$ :Observable<boolean>;
  inventorySettings$ :Observable<SystemSettingInterface>;

  weightUnits :OptionInterface[] = [
    { label: 'lbs', value: 'pounds' },
    { label: 'oz', value: 'ounces' },
    { label: 'g', value: 'grams' },
  ];
  dimensionUnits :OptionInterface[] = [
    { label: 'in', value: 'inches' },
    { label: 'cm', value: 'centimeters' },
  ];

  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data :IToShippingAddress,
    private dialogRef :MatDialogRef<ShippingRatesComponent>,
    private snackBar :MatSnackBar,
    private fb :UntypedFormBuilder,
    private router :Router,
    private systemSettingService :SystemSettingApiService,
    private locationService :LocationApiService,
    private shipStationService :ShipstationApiService,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.systemSettingService.getInventorySettings();
    this.shipStationService.getCarriers();
    this.getDataFromService();
    this.getInventorySettingsFromService().subscribe();
    this.getWarehouseFromService().subscribe();
    this.getCarriersFromService().subscribe();
    this.getPackagesFromService().subscribe();
    this.getServicesFromService().subscribe();
    this.getRatesFromService().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onChangeTab(tabs :MatTabGroup, index :number) :void {
    tabs.selectedIndex = index;
  }

  toWarehouse() :void {
    this.onClose();
    this.router.navigate(['locations', 'warehouses']);
  }

  onSetUnit(field :string, unit :string) :void {
    this.form.get(field)?.patchValue(unit);
  }

  onSelectShippingRate(rate :IShipstationRates) :void {
    this.selectedRate = rate;
  }

  onSetShippingRate() :void {
    this.dialogRef.close(this.selectedRate);
  }

  onSelectCarrier(event :MatSelectChange) :void {
    const params = { carrierCode: event.value };
    this.shipStationService.getPackages(params);
    this.shipStationService.getServices(params);
  }

  onSubmit(tabs :MatTabGroup) :void {
    if (this.form.valid) {
      const data = this.form.value;
      const payload = {
        ...data,
        weight: this.weight,
        toCountry: !data.toCountry ? 'US' : data.toCountry,
      };

      if (!payload.toPostalCode) {
        this.snackBar.open(
          'Please ensure to fill in the postal code field in your shipping address',
          'Error',
          {
            duration: SnackBarConfig.DEFAULT.duration,
          },
        );

        return;
      }

      this.shipStationService
        .getRates(payload)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (rates :IShipstationRates[]) => {
            if (rates && rates.length === 1) {
              this.dialogRef.close(rates[0]);
            } else {
              this.onChangeTab(tabs, 1);
            }
          },
          error: (err) => {
            this.snackBar.open(err.error, 'Error', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    }
  }

  validateWeight(controlName :string) :void {
    const weightControl = this.form.get('weight');

    if (weightControl) {
      const subControl = weightControl.get(controlName);

      if (subControl && subControl.value === null) {
        subControl.patchValue(0);
      }
    }
  }

  private initForm() :void {
    this.form = this.fb.group({
      carrierCode: ['', Validators.required],
      serviceCode: [''],
      packageCode: [''],
      fromPostalCode: ['', Validators.required],
      toState: [this.data.toState || ''],
      toCountry: [this.data.toCountry || ''],
      toPostalCode: [this.data.toPostalCode || ''],
      toCity: [this.data.toCity || ''],
      weight: this.fb.group({
        pounds: this.fb.group({
          units: [EWeightUnit.POUNDS],
          value: [0, [Validators.required]],
        }),
        ounces: this.fb.group({
          units: [EWeightUnit.OUNCES],
          value: [0, [Validators.required]],
        }),
      }),
      dimensions: this.fb.group({
        units: ['inches'],
        length: [0],
        width: [0],
        height: [0],
      }),
      confirmation: ['none'],
      residential: [false],
    });
  }

  private getDataFromService() :void {
    this.inventorySettings$ = this.systemSettingService.inventorySettings$;
    this.warehouse$ = this.locationService.warehouse$;
    this.carriers$ = this.shipStationService.carriers$;
    this.packages$ = this.shipStationService.packages$;
    this.services$ = this.shipStationService.services$;
    this.rates$ = this.shipStationService.rates$;
    this.loading$ = this.locationService.loading$;
    this.loadingRates$ = this.shipStationService.loading$;
  }

  private getInventorySettingsFromService() :Observable<SystemSettingInterface> {
    return this.inventorySettings$.pipe(
      tap((settings :SystemSettingInterface) => {
        const { value } = settings;
        if (value) {
          const inventorySettings = JSON.parse(value);
          this.locationService.getWarehouseById(inventorySettings?.warehouse_id);
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getWarehouseFromService() :Observable<IWarehouse> {
    return this.warehouse$.pipe(
      tap((warehouse :IWarehouse) => {
        if (warehouse) {
          this.warehouse = warehouse;
          this.form.patchValue({
            fromPostalCode: warehouse?.zip,
          });
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getCarriersFromService() :Observable<IShipStationCarriers[]> {
    return this.carriers$.pipe(
      tap((response :any) => {
        const { data } = response;
        if (data) {
          this.carriers = data;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getPackagesFromService() :Observable<IShipstationPackagesServices[]> {
    return this.packages$.pipe(
      tap((response :any) => {
        const { data } = response;
        if (data) {
          this.packages = data;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getServicesFromService() :Observable<IShipstationPackagesServices[]> {
    return this.services$.pipe(
      tap((response :any) => {
        const { data } = response;
        if (data) {
          this.services = data;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getRatesFromService() :Observable<IShipstationRates[]> {
    return this.rates$.pipe(
      tap((rates :IShipstationRates[]) => {
        if (rates) {
          this.rates = rates;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get invalidLocationAddress() :boolean {
    if (!this.warehouse) {
      return false;
    }

    return (
      !this.warehouse?.state ||
      !this.warehouse?.country ||
      !this.warehouse?.zip ||
      !/^-?\d+$/.test(this.warehouse?.zip) ||
      !this.warehouse?.city
    );
  }

  get confirmationOptions() :string[] {
    let options = ['none', 'delivery', 'signature', 'adult_signature'];

    if (this.form.get('carrierCode')?.value === 'fedex') {
      options.push('direct_signature');
    } else {
      if (options.includes('direct_signature')) {
        options = options.filter((option) => option !== 'direct_signature');
      }
    }

    return options;
  }

  get weightUnit() :string {
    const unit = this.form.get('weight.units')?.value;
    const weightUnit = this.weightUnits.find((obj) => obj.value === unit);

    return weightUnit?.label || '';
  }

  get dimensionUnit() :string {
    const unit = this.form.get('dimensions.units')?.value;
    const dimensionUnit = this.dimensionUnits.find((obj) => obj.value === unit);

    return dimensionUnit?.label || '';
  }

  get hasInvalidInput() :boolean {
    const invalid =
      this.form.get('carrierCode')?.invalid ||
      this.form.get('weight.value')?.invalid;

    return invalid || false;
  }

  get carrierCodeControl() :UntypedFormControl {
    return this.form.get('carrierCode') as UntypedFormControl;
  }

  get weightValueControl() :UntypedFormControl {
    return this.form.get('weight.value') as UntypedFormControl;
  }

  get weight() :IWeight {
    const pounds = this.form.get('weight.pounds')?.value;
    const ounces = this.form.get('weight.ounces')?.value;

    if (ounces?.value === null || ounces?.value === 0) {
      return pounds;
    }

    if (pounds?.value > 0) {
      return pounds;
    }

    return ounces;
  }
}
